// ---------------------------------------------------------------------------
// The tools, grouped the way an edit actually proceeds: the clip, then the
// frame, then what sits over it, then the sound.
//
// Each one names a control that exists somewhere in this workspace, and
// clicking it takes you there. It is a map, not a second way of doing things —
// a rail whose buttons opened their own dialogs would be a parallel editor that
// has to be kept in step with the panels.
//
// Two of them are disabled on purpose, and that is the honest version of this
// screen. `merge` and `compress` are in the design and have nothing behind
// them: scenes are always joined, so there is no merge to perform, and nothing
// in the render plane exposes an output quality. Showing them greyed with a
// reason says where the product actually is. Leaving them out would say the
// same thing less clearly; showing them live would be four dead buttons, which
// is the thing this codebase keeps refusing to ship.
// ---------------------------------------------------------------------------

export interface Tool {
  id: string;
  label: string;
  glyph: string;
  /** Where this control lives. Null when nothing implements it yet. */
  target: string | null;
  /** Why it is unavailable, said plainly. */
  why?: string;
  /** Only meaningful with a scene selected. */
  needsScene?: boolean;
}

export const TOOL_GROUPS: Array<{ name: string; tools: Tool[] }> = [
  {
    name: 'Clip',
    tools: [
      { id: 'cut', label: 'cut', glyph: '✂', target: 'sourceIn', needsScene: true },
      { id: 'trim', label: 'trim', glyph: '⇥', target: 'sourceOut', needsScene: true },
      {
        id: 'merge', label: 'merge', glyph: '⧉', target: null,
        why: 'Scenes are already joined end to end — there is nothing to merge. '
           + 'Change how they join on the timeline.',
      },
      { id: 'speed', label: 'speed', glyph: '⏩', target: 'speed', needsScene: true },
    ],
  },
  {
    name: 'Frame',
    tools: [
      { id: 'crop', label: 'crop', glyph: '⬚', target: 'crop', needsScene: true },
      { id: 'resize', label: 'resize', glyph: '▭', target: 'shorts' },
      { id: 'shot', label: 'shot', glyph: '▣', target: 'shot', needsScene: true },
      { id: 'motion', label: 'motion', glyph: '◈', target: 'motion', needsScene: true },
    ],
  },
  {
    name: 'Layer',
    tools: [
      { id: 'text', label: 'text', glyph: 'T', target: 'layers', needsScene: true },
      { id: 'backdrop', label: 'backdrop', glyph: '▤', target: 'mix' },
      { id: 'director', label: 'suggest', glyph: '✦', target: 'director' },
    ],
  },
  {
    name: 'Sound',
    tools: [
      { id: 'music', label: 'music', glyph: '♪', target: 'mix' },
      { id: 'voice', label: 'voice', glyph: '◉', target: 'speaker', needsScene: true },
      // The words are still arithmetic from the script; where they sit, how
      // big, on a plate or outlined, and in which face are the card's (086).
      { id: 'captions', label: 'captions', glyph: '≡', target: 'captions' },
    ],
  },
  {
    name: 'Set',
    tools: [
      { id: 'history', label: 'history', glyph: '↺', target: 'history' },
      // Draft, standard or high (086): a preset, a CRF and an audio bitrate
      // on the final encodes, chosen in the mix.
      { id: 'compress', label: 'quality', glyph: '⤓', target: 'quality' },
    ],
  },
];

export function ToolRail({ active, hasScene, onPick }: {
  active: string | null;
  hasScene: boolean;
  onPick: (tool: Tool) => void;
}) {
  return (
    <nav className="rail" aria-label="Tools">
      {TOOL_GROUPS.map((g) => (
        <div className="rail-grp" key={g.name}>
          <p className="rail-name">{g.name}</p>
          {g.tools.map((t) => {
            // Two different reasons to be unavailable, and they are not the
            // same thing to a creator: "nothing implements this" is permanent
            // and "pick a scene first" is a next step.
            const unbuilt = t.target === null;
            const waiting = !unbuilt && Boolean(t.needsScene) && !hasScene;
            return (
              <button
                key={t.id}
                className={`rail-btn${active === t.id ? ' is-on' : ''}${unbuilt ? ' is-unbuilt' : ''}`}
                disabled={unbuilt || waiting}
                title={unbuilt ? t.why : waiting ? 'Select a scene on the timeline first' : t.label}
                onClick={() => onPick(t)}
              >
                <span className="rail-glyph" aria-hidden="true">{t.glyph}</span>
                <span className="rail-lbl">{t.label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
